import { FC } from "react"

import { SaveButton } from "components/buttons/SaveButton"

import { ResetToDefaultButton } from "./ResetToDefaultButton"

/** A resettable group owned by this section; `id` must exist in the backend reset registry. */
export interface SectionResetTarget {
  id: string
  label: string
}

interface SettingsSectionActionsProps {
  onSave: () => void | Promise<void>
  saving?: boolean
  saveDisabled?: boolean
  /** Single group shorthand. Ignored when `resetTargets` is given. */
  resetTarget?: string
  resetLabel?: string
  /** For a section that owns more than one group, such as Withdrawals (withdrawals + KYC). */
  resetTargets?: SectionResetTarget[]
  onResetDone?: () => void | Promise<void>
}

/**
 * The footer every settings section uses: one primary Save, and — for each group the section
 * owns — one destructive Reset beside it.
 *
 * Keeping both in one component is what stops a destructive action from being rendered with
 * the Save button's styling.
 */
export const SettingsSectionActions: FC<SettingsSectionActionsProps> = ({
  onSave,
  saving = false,
  saveDisabled = false,
  resetTarget,
  resetLabel,
  resetTargets,
  onResetDone
}) => {
  const targets = resetTargets ?? (resetTarget && resetLabel ? [{ id: resetTarget, label: resetLabel }] : [])

  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {targets.map(({ id, label }) => (
        <ResetToDefaultButton key={id} target={id} label={label} onDone={onResetDone} showSectionInLabel={targets.length > 1} />
      ))}
      <SaveButton onClick={onSave} loading={saving} disabled={saveDisabled} className="w-auto min-w-[12rem] mr-0" />
    </div>
  )
}
